import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

export type SystemMember = {
  id: string;
  email: string;
  nickname: string;
  tier: 'FREE' | 'PRO';
  signupType: string;
  isSystemAdmin: boolean;
  createdAt: string;
  lastAccessedAt: string | null;
  subscriptionCurrentPeriodEndAt: string | null;
  stats?: {
    agentConfigs: number;
    runners: number;
  };
};

type MembersPagedResponse = {
  data: SystemMember[];
  meta: PaginationMeta;
};

type MemberResponse = {
  data: {
    id: string;
    email: string;
    nickname: string;
    tier: 'FREE' | 'PRO';
    createdAt: string;
    updatedAt: string;
  };
};

export const fetchMembersPaged = async (
  token: string,
  params?: {
    search?: string;
    tier?: 'FREE' | 'PRO';
    signupType?: 'GOOGLE' | 'MICROSOFT' | 'GITHUB' | 'APPLE' | 'EMAIL';
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
    includeStats?: boolean;
  },
): Promise<PagedData<SystemMember>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.set('search', params.search);
  if (params?.tier) queryParams.set('tier', params.tier);
  if (params?.signupType) queryParams.set('signupType', params.signupType);
  if (params?.from) queryParams.set('from', params.from);
  if (params?.to) queryParams.set('to', params.to);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
  if (params?.includeStats) queryParams.set('includeStats', 'true');

  const url = `${getApiUrl()}/api/members${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch members: ${response.status}`);
  }

  const data = (await response.json()) as MembersPagedResponse;
  return data;
};

export const updateMemberTier = async (token: string, memberId: string, tier: 'FREE' | 'PRO') => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/members/${memberId}/tier`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify({ tier }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update member tier: ${response.status}`);
  }

  return ((await response.json()) as MemberResponse).data;
};

export const updateMemberSystemAdmin = async (token: string, memberId: string, isSystemAdmin: boolean) => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/members/${memberId}/system-admin`, {
    method: 'PATCH',
    headers: authHeaders(token, true),
    body: JSON.stringify({ isSystemAdmin }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update system admin: ${response.status}`);
  }

  return ((await response.json()) as MemberResponse).data;
};
